import { Alert, Descriptions, Progress, Space, Tag, Typography } from "antd";

const { Text } = Typography;
const ACTIVE_STATUSES = new Set(["QUEUED", "LEASED", "RUNNING"]);

export const isActiveAiVideoStatus = (status) => ACTIVE_STATUSES.has(status);

export function createAiVideoProjectPoller({ fetchProject, onProject, onError, intervalMs = 3000, isCurrent = () => true }) {
  let generation = 0;
  let timer = null;
  let activeId = null;
  let requesting = false;
  let confirmedActive = false;

  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const stop = () => {
    generation += 1;
    activeId = null;
    requesting = false;
    confirmedActive = false;
    clearTimer();
  };

  const request = async (requestGeneration, projectId) => {
    if (requesting || requestGeneration !== generation || projectId !== activeId || !isCurrent()) return;
    requesting = true;
    try {
      const project = await fetchProject(projectId);
      if (requestGeneration !== generation || projectId !== activeId || !isCurrent()) return;
      onProject(project);
      confirmedActive = isActiveAiVideoStatus(project?.status);
      if (confirmedActive) {
        timer = setTimeout(() => request(requestGeneration, projectId), intervalMs);
      }
    } catch (error) {
      if (requestGeneration !== generation || projectId !== activeId || !isCurrent()) return;
      onError(error);
      if (confirmedActive) timer = setTimeout(() => request(requestGeneration, projectId), intervalMs);
    } finally {
      if (requestGeneration === generation && projectId === activeId) requesting = false;
    }
  };

  const start = (projectId, knownStatus) => {
    generation += 1;
    clearTimer();
    activeId = projectId;
    requesting = false;
    confirmedActive = isActiveAiVideoStatus(knownStatus);
    request(generation, projectId);
  };

  return { start, stop };
}

export const aiVideoErrorMessage = (errorCode, targetDurationSeconds) => {
  const duration = Number(targetDurationSeconds) || 0;
  return ({
    PROVIDER_AUTH: "API key AI không hợp lệ, đã hết hạn hoặc không có quyền gọi model.",
    PROVIDER_RATE_LIMIT: "Provider AI đang giới hạn lưu lượng hoặc đã hết quota. Hãy chờ rồi thử lại.",
    PROVIDER_INVALID_OUTPUT: "Provider AI trả dữ liệu sai định dạng yêu cầu sau nhiều lần thử.",
    PROVIDER_MODEL_NOT_FOUND: "Model AI đã cấu hình không tồn tại hoặc tài khoản không được quyền sử dụng model đó.",
    PROVIDER_UNAVAILABLE: "Không thể kết nối hoặc sử dụng provider AI đã cấu hình.",
    SCRIPT_TOO_SHORT: `Kịch bản AI tạo ra ngắn hơn thời lượng tối thiểu${duration ? ` ${duration} giây` : ""} sau 3 lần viết lại.`,
    STOCK_UNAVAILABLE: "Không tìm hoặc tải được tư liệu phù hợp từ Pexels/Pixabay.",
    VOICE_GENERATION_FAILED: "Không thể tạo hoặc chuẩn hóa file giọng đọc.",
    SUBTITLE_GENERATION_FAILED: "Không thể tạo phụ đề hoặc thời gian phụ đề không khớp giọng đọc.",
    COMPOSITION_FAILED: "FFmpeg không thể dựng hoàn chỉnh video từ hình, tiếng và phụ đề.",
    QUALITY_CHECK_FAILED: "Video đã dựng nhưng không đạt kiểm tra chất lượng hình, tiếng hoặc thời lượng.",
    ARTIFACT_UPLOAD_FAILED: "Render đã xong nhưng không thể tải file video lên kho lưu trữ.",
    TIMEOUT: "Quá trình xử lý vượt quá thời gian cho phép.",
    CANCELLED: "Lần thực hiện đã được yêu cầu dừng.",
    RENDER_FAILED: "Quá trình tạo video gặp lỗi chưa xác định. Vui lòng xem log Render Agent.",
  }[String(errorCode || "").toUpperCase()] || "Quá trình tạo video gặp lỗi chưa xác định.");
};

export const sanitizedProjectError = (project) => project?.status === "FAILED"
  ? aiVideoErrorMessage(project.errorMessage, project.targetDurationSeconds)
  : null;

export const aiVideoStatusLabel = (status) => ({
  DRAFT: "Bản nháp", QUEUED: "Đang chờ", LEASED: "Đã nhận job", RUNNING: "Đang chạy",
  COMPLETED: "Hoàn tất", FAILED: "Thất bại", CANCELLED: "Đã hủy",
}[status] || "Không xác định");

export const aiVideoStageLabel = (stage) => ({
  RESEARCH: "Nghiên cứu", SCRIPT: "Viết kịch bản", STORYBOARD: "Storyboard", ASSET_SEARCH: "Tìm tư liệu",
  ASSET_DOWNLOAD: "Tải tư liệu", VOICE: "Tạo giọng đọc", SUBTITLE: "Tạo phụ đề", COMPOSING: "Dựng video",
  QUALITY_CHECK: "Kiểm tra chất lượng", UPLOADING: "Chuẩn bị đầu ra", COMPLETED: "Hoàn tất",
  FAILED: "Thất bại", CANCELLED: "Đã hủy",
}[stage] || "Chưa xác định");

export default function AiVideoProgress({ project }) {
  const percent = Math.max(0, Math.min(100, Number(project?.progressPercent) || 0));
  const error = sanitizedProjectError(project);
  return <Space direction="vertical" size="middle" style={{ width: "100%" }}>
    <Progress percent={percent} status={project?.status === "FAILED" ? "exception" : undefined} />
    <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
      <Descriptions.Item label="Trạng thái"><Tag>{aiVideoStatusLabel(project?.status)}</Tag></Descriptions.Item>
      <Descriptions.Item label="Job hiện tại"><Text type="secondary">API Task 4 chưa cung cấp bản ghi job hiện tại</Text></Descriptions.Item>
      <Descriptions.Item label="Giai đoạn">{aiVideoStageLabel(project?.currentStage)}</Descriptions.Item>
      <Descriptions.Item label="Generation">{Number(project?.generationNumber) || 0}</Descriptions.Item>
      <Descriptions.Item label="Tiến độ cảnh"><Text type="secondary">API Task 4 chưa cung cấp dữ liệu cảnh</Text></Descriptions.Item>
      <Descriptions.Item label="Đầu ra"><Text type="secondary">Chưa khả dụng cho đến Backend Task 6</Text></Descriptions.Item>
      <Descriptions.Item label="Tạo lúc">{project?.createdAt ? new Date(project.createdAt).toLocaleString("vi-VN") : "-"}</Descriptions.Item>
      <Descriptions.Item label="Cập nhật lúc">{project?.updatedAt ? new Date(project.updatedAt).toLocaleString("vi-VN") : "-"}</Descriptions.Item>
    </Descriptions>
    {error && <Alert type="error" showIcon message={error} />}
  </Space>;
}
